import { useCallback, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { buoc6Api } from '@/services/performance/target-api';
import { usePrivateStore } from '@/store/usePrivateStore';
import { useBuoc6ChiTieuWarningHeaderStore } from '@/store/buoc6ChiTieuWarningHeaderStore';
import { useGetPhanHe } from '@/hooks/useGetPhanHe';
import CommonJs from '@/utils/commonJs';
import { CONSTANTS } from '@/utils/constants';
import type { RequestHeader } from '@/types/base';

function buildWarningRequestHeader(
  header: RequestHeader | null | undefined,
  year: number,
  giaiDoanId?: string | null
): RequestHeader {
  return {
    ...header,
    phanHe: header?.phanHe || 'bsc',
    selectPeriod: {
      ...header?.selectPeriod,
      year,
      idGiaiDoan: header?.selectPeriod?.idGiaiDoan ?? giaiDoanId ?? undefined,
    },
    selectTree: {
      ...header?.selectTree,
      idSelected: header?.selectTree?.idSelected,
      idParentSelected: header?.selectTree?.idParentSelected,
      isEmployee: header?.selectTree?.isEmployee ?? false,
      isCompany: header?.selectTree?.isCompany ?? false,
    },
  };
}

export function useBuoc6ChiTieuWarningHeader(
  enabled: boolean,
  options?: { skipAutoFetch?: boolean }
) {
  const router = useRouter();
  const phanHe = useGetPhanHe();
  const isBsc = String(phanHe || '').toLowerCase() === CONSTANTS.PHAN_HE.BSC;
  // Chỉ fetch/hiển thị trên phân hệ BSC
  const effectiveEnabled = enabled && isBsc;

  const { getRequestHeader, giaiDoan } = usePrivateStore();
  const header = getRequestHeader();
  const departmentId = header?.selectTree?.idSelected || '';
  const isEmployeeNode = !!header?.selectTree?.isEmployee;
  const year = giaiDoan.year ?? header?.selectPeriod?.year ?? new Date().getFullYear();
  const isBuoc6TablePage = router.pathname === '/buoc6' || router.pathname === '/buoc6/';
  const isBuoc6Page = typeof router.pathname === 'string' && router.pathname.includes('buoc6');
  const isChiTieuChaConPage =
    typeof router.pathname === 'string' && router.pathname.includes('chi-tieu-cha-con');

  const warnings = useBuoc6ChiTieuWarningHeaderStore((s) => s.warnings);
  const activeWarnings = useBuoc6ChiTieuWarningHeaderStore((s) => s.activeWarnings);
  const loading = useBuoc6ChiTieuWarningHeaderStore((s) => s.loading);
  const fetchSeqRef = useRef(0);

  useEffect(() => {
    if (!effectiveEnabled) {
      useBuoc6ChiTieuWarningHeaderStore.getState().clear();
    }
  }, [effectiveEnabled]);

  const fetchWarnings = useCallback(async () => {
    const currentHeader = getRequestHeader();
    const currentDeptId = currentHeader?.selectTree?.idSelected || '';
    const currentYear =
      giaiDoan.year ?? currentHeader?.selectPeriod?.year ?? new Date().getFullYear();

    if (
      !effectiveEnabled ||
      !currentDeptId ||
      isEmployeeNode ||
      CommonJs.checkNullIDSelectRequestHeader(currentHeader)
    ) {
      useBuoc6ChiTieuWarningHeaderStore.getState().clear();
      return;
    }

    const requestHeader = buildWarningRequestHeader(currentHeader, currentYear, giaiDoan.id);

    const seq = ++fetchSeqRef.current;
    useBuoc6ChiTieuWarningHeaderStore.getState().beginFetch(currentDeptId);

    try {
      const res: any = await buoc6Api.getChiTieuChildWarnings({
        requestHeader,
        tanSuat: ',2,3,4,5,6,',
        showKPI: 'an',
      });
      if (fetchSeqRef.current !== seq) return;

      const payload = res?.data?.data ?? res?.data ?? res;
      useBuoc6ChiTieuWarningHeaderStore.getState().setFromFetch(currentDeptId, payload);
    } catch {
      if (fetchSeqRef.current !== seq) return;
      useBuoc6ChiTieuWarningHeaderStore.getState().clear();
    }
  }, [
    effectiveEnabled,
    departmentId,
    isEmployeeNode,
    year,
    giaiDoan.id,
    giaiDoan.year,
    getRequestHeader,
  ]);

  useEffect(() => {
    if (!effectiveEnabled || isBuoc6TablePage || isChiTieuChaConPage || options?.skipAutoFetch) return;
    void fetchWarnings();
  }, [
    effectiveEnabled,
    isBuoc6TablePage,
    isChiTieuChaConPage,
    options?.skipAutoFetch,
    fetchWarnings,
    departmentId,
    year,
    router.pathname,
  ]);

  return {
    warnings: effectiveEnabled ? warnings : [],
    activeWarnings: effectiveEnabled ? activeWarnings : [],
    activeCount: effectiveEnabled ? activeWarnings.length : 0,
    loading: effectiveEnabled && loading,
    refresh: fetchWarnings,
    isBuoc6Page,
    isChiTieuChaConPage,
    isBsc,
    featureEnabled: effectiveEnabled,
  };
}
